import {
  arrayToList,
  arrayToTable,
  type KtList,
  listableToArray,
  type Schema,
  type Table,
} from "@ostack.tech/kform";

/** Supported listable types. */
export type Listable<T = unknown> = T[] | KtList<T> | Table<T>;

/**
 * Formats a listable {@link value} as an array, given its {@link schema}.
 *
 * @param value Listable value to format as an array.
 * @param schema Schema of the listable {@link value}.
 * @returns Array representation of the listable {@link value}.
 */
export function formatListableAsArray<
  T = unknown,
  TListable extends Listable<T> | null = Listable<T> | null,
  // Not using the `schema` is an implementation detail
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
>(value: TListable | undefined, schema: Schema<TListable>): T[] | null {
  return value == null ? null : listableToArray(value);
}

/**
 * Parses a listable [value]{@link formattedValue} formatted as an array as a
 * listable value, given the {@link schema} of the expected listable value.
 *
 * @param formattedValue Array value to parse as a listable value.
 * @param schema Schema of the expected listable value.
 * @returns Listable representation of the [formatted
 *   value]{@link formattedValue} according to {@link schema}.
 */
export function parseArrayAsListable<
  T = unknown,
  TListable extends Listable<T> | null = Listable<T> | null,
>(formattedValue: T[] | null, schema: Schema<TListable>): TListable {
  const supportsNull = schema.typeInfo.nullable;
  const schemaType = schema.typeInfo.name;
  if (formattedValue == null) {
    if (supportsNull) {
      return null as TListable;
    } else {
      formattedValue = [];
    }
  }

  switch (schemaType) {
    case "Array":
      return formattedValue as TListable;
    case "List":
      return arrayToList(formattedValue) as TListable;
    default: // `Table`
      return arrayToTable(formattedValue) as TListable;
  }
}
